import { useState, useEffect } from 'react';

type TScript = {
  addEventListener: (type: string, func: () => void) => void;
  removeEventListener: (type: string, func: () => void) => void;
};

const cachedScripts: Record<
  string,
  { isScriptLoaded: boolean; script: TScript }
> = {};

function useScript(src: string) {
  const [state, setState] = useState({
    isScriptLoaded: false,
    hasError: false,
  });

  useEffect(() => {
    const onScriptLoad = () => {
      cachedScripts[src].isScriptLoaded = true;
      setState({
        isScriptLoaded: true,
        hasError: false,
      });
    };

    const onScriptError = () => {
      delete cachedScripts[src];

      setState({
        isScriptLoaded: true,
        hasError: true,
      });
    };

    const scriptLoader = cachedScripts[src];
    if (scriptLoader) {
      if (scriptLoader.isScriptLoaded) {
        setState({
          isScriptLoaded: true,
          hasError: false,
        });
      } else {
        const { script } = scriptLoader;
        script.addEventListener('load', onScriptLoad);
        script.addEventListener('error', onScriptError);
        return () => {
          script.removeEventListener('load', onScriptLoad);
          script.removeEventListener('error', onScriptError);
        };
      }
    } else {
      const script = document.createElement('script');
      script.src = src;
      script.async = true;

      script.addEventListener('load', onScriptLoad);
      script.addEventListener('error', onScriptError);

      document.body.appendChild(script);

      cachedScripts[src] = { isScriptLoaded: false, script };
      return () => {
        script.removeEventListener('load', onScriptLoad);
        script.removeEventListener('error', onScriptError);
      };
    }
  }, [src]);

  return [state.isScriptLoaded, state.hasError];
}

export default useScript;
